import { Button, CircularProgress, Divider, TextField, Paper, IconButton, InputAdornment, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { Visibility, VisibilityOff, EmailOutlined, LockOutlined } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../State/Store';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import { loginWithGoogle, signin } from '../../../State/AuthSlice';
import { useNavigate } from 'react-router-dom';
import api from '../../../config/Api';

declare global {
    interface Window {
        google?: any;
    }
}

interface LoginFormProps {
    onToggleAuth?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleAuth }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { auth } = useAppSelector(store => store);
    const [showPassword, setShowPassword] = useState(false);

    // Reset Password Modal State
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetMessage, setResetMessage] = useState<{ text: string; severity: "success" | "error" } | null>(null);
    const [resetLoading, setResetLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            email: "",
            password: ""
        },
        validationSchema: Yup.object({
            email: Yup.string().required("Email or Username is required"),
            password: Yup.string().required("Password is required")
        }),
        onSubmit: (values) => {
            const formattedPayload = {
                email: values.email.trim(),
                password: values.password
            };
            dispatch(signin({ loginRequest: formattedPayload, navigate }));
        }
    });

    useEffect(() => {
        const initializeGoogle = () => {
            if (window.google) {
                window.google.accounts.id.initialize({
                    client_id: "1034555363194-vq6s34pv4qmkladc9vh50lor3fbbs9te.apps.googleusercontent.com",
                    callback: (response: any) => {
                        if (response && response.credential) {
                            dispatch(loginWithGoogle({ idToken: response.credential, navigate }));
                        }
                    },
                    auto_select: false
                });
            }
        };

        if (window.google) {
            initializeGoogle();
        } else {
            window.addEventListener('load', initializeGoogle);
            return () => window.removeEventListener('load', initializeGoogle);
        }
    }, [dispatch, navigate]);

    const handleGoogleLoginClick = () => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
            window.google.accounts.id.prompt((notification: any) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    window.google.accounts.id.prompt();
                }
            });
        }
    };

    const handleInitiatePasswordReset = async () => {
        if (!resetEmail || !resetEmail.includes("@")) {
            setResetMessage({ text: "Please enter a valid email address.", severity: "error" });
            return;
        }

        setResetLoading(true);
        setResetMessage(null);

        try {
            await api.post("/auth/reset-password", { email: resetEmail.trim() });
            setResetMessage({
                text: "A new temporary 10-character password has been generated and sent to your email.",
                severity: "success",
            });
        } catch (error: any) {
            setResetMessage({
                text: error.response?.data?.message || "Failed to reset password. Please ensure the email is registered.",
                severity: "error",
            });
        } finally {
            setResetLoading(false);
        }
    };

    const GoogleIcon = () => (
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
    );

    return (
        <Paper elevation={4} className="w-full max-w-md p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-xl bg-white space-y-5">
            <div className="text-center space-y-1">
                <h1 className="font-extrabold text-2xl sm:text-3xl text-[#24248a] tracking-tight">
                    Welcome Back
                </h1>
                <p className="text-xs sm:text-sm text-gray-500">Sign in to access your account</p>
            </div>

            {auth.error && (
                <Alert severity="error" className="rounded-lg text-xs sm:text-sm">
                    {auth.error}
                </Alert>
            )}

            <form onSubmit={formik.handleSubmit} className="space-y-4">
                <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email or Username"
                    variant="outlined"
                    size="medium"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailOutlined sx={{ color: '#24248a', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            '&.Mui-focused fieldset': { borderColor: '#24248a' }
                        }
                    }}
                />

                <TextField
                    fullWidth
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    variant="outlined"
                    size="medium"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockOutlined sx={{ color: '#24248a', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                    {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            '&.Mui-focused fieldset': { borderColor: '#24248a' }
                        }
                    }}
                />

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={() => {
                            setResetMessage(null);
                            setResetEmail("");
                            setOpenResetDialog(true);
                        }}
                        className="text-xs text-[#24248a] hover:underline font-semibold"
                    >
                        Forgot Password?
                    </button>
                </div>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={auth.loading}
                    disableElevation
                    sx={{
                        py: "12px",
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        backgroundColor: "#24248a",
                        borderRadius: "10px",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#1b1b6d" }
                    }}
                >
                    {auth.loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
                </Button>

                <Divider sx={{ my: 1.5, fontSize: "0.75rem", color: "text.secondary", fontWeight: 600 }}>
                    OR
                </Divider>

                {/* Google Sign In Button Container */}
                <div className="space-y-3">
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleGoogleLoginClick}
                        startIcon={<GoogleIcon />}
                        sx={{
                            py: "10px",
                            borderColor: "#dadce0",
                            color: "#3c4043",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "10px",
                            fontSize: "0.9rem",
                            "&:hover": {
                                backgroundColor: "#f8f9fa",
                                borderColor: "#cbd5e1"
                            }
                        }}
                    >
                        {auth.loading ? <CircularProgress size={20} color="inherit" /> : "Sign in with Google"}
                    </Button>

                    {/* Appended Auth Switch Action */}
                    <div className="flex items-center justify-center gap-1.5 pt-1 text-xs sm:text-sm text-gray-600">
                        <span>Don't have an account?</span>
                        <Button
                            size="small"
                            onClick={onToggleAuth}
                            sx={{
                                color: "#24248a",
                                fontWeight: 700,
                                textTransform: "none",
                                fontSize: "0.875rem",
                                minWidth: "auto",
                                padding: "2px 6px",
                                "&:hover": { backgroundColor: "#f1f1f9" }
                            }}
                        >
                            Create Account
                        </Button>
                    </div>
                </div>
            </form>

            {/* Password Reset Modal Dialog */}
            <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle className="font-bold">Reset Password</DialogTitle>
                <DialogContent className="space-y-4 pt-2">
                    <Typography variant="body2" className="text-slate-500">
                        Enter your registered email address to generate and receive a new temporary 10-character password.
                    </Typography>
                    {resetMessage && (
                        <Alert severity={resetMessage.severity} sx={{ fontSize: "0.85rem" }}>
                            {resetMessage.text}
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        size="small"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions className="p-4">
                    <Button onClick={() => setOpenResetDialog(false)} color="inherit" size="small">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleInitiatePasswordReset}
                        variant="contained"
                        disabled={resetLoading}
                        size="small"
                        sx={{ backgroundColor: "#24248a", "&:hover": { backgroundColor: "#1b1b6d" } }}
                    >
                        {resetLoading ? <CircularProgress size={20} color="inherit" /> : "Reset Password"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default LoginForm;